import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import communityI18n from '../utils/communityI18n';

/**
 * Global EN ⇄ HI language context for the entire community app.
 *
 * Persistence: localStorage under `community_lang`. The choice survives page
 * reloads, route changes between onboarding steps, and even logout/login.
 *
 * Usage:
 *   const { lang, toggleLang, t } = useCommunityLang();
 *   <button>{t('common.continue')}</button>
 *
 * `t(key)` looks up the dotted key in the dictionary; if the Hindi value
 * doesn't exist it falls back to the English value so the UI never breaks.
 */
const CommunityLangContext = createContext(null);
const STORAGE_KEY = 'community_lang';

export const CommunityLangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'hi' || stored === 'en' ? stored : 'en';
    } catch { return 'en'; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* noop */ }
  }, [lang]);

  const setLang = useCallback((l) => { if (l === 'hi' || l === 'en') setLangState(l); }, []);
  const toggleLang = useCallback(() => setLangState((p) => (p === 'en' ? 'hi' : 'en')), []);

  const t = useCallback((key, fallback) => {
    const path = String(key).split('.');
    let en = communityI18n.en;
    let hi = communityI18n.hi;
    for (const seg of path) {
      en = en && typeof en === 'object' ? en[seg] : undefined;
      hi = hi && typeof hi === 'object' ? hi[seg] : undefined;
    }
    if (lang === 'hi' && typeof hi === 'string') return hi;
    if (typeof en === 'string') return en;
    return fallback ?? key;
  }, [lang]);

  return (
    <CommunityLangContext.Provider value={{ lang, setLang, toggleLang, t }}>
      {children}
    </CommunityLangContext.Provider>
  );
};

export const useCommunityLang = () => {
  const ctx = useContext(CommunityLangContext);
  // Safe fallback so a stray usage outside the provider doesn't crash —
  // returns English with a no-op toggle.
  if (!ctx) return { lang: 'en', setLang: () => {}, toggleLang: () => {}, t: (_k, f) => f ?? _k };
  return ctx;
};

export default CommunityLangContext;
